import type { Session, User } from '@supabase/supabase-js';
import { RECAPTCHA_ACTIONS } from '../../supabase/functions/_shared/recaptchaActions';
import { RECAPTCHA_MODE_HEADER } from '../../supabase/functions/_shared/recaptchaMode';
import { buildFunctionUrl, getSupabasePublishableKey } from '@/lib/selfHostedUrls';
import {
  createRecaptchaRequestProof,
  recaptchaTokenPayload,
  RECAPTCHA_PUBLIC_ERROR,
} from '@/lib/recaptchaRequest';
import { sanitizeForkSeekContinuation } from '../../supabase/functions/_shared/forkSeekAuthContinuation';

type Portal = 'hire' | 'seek' | 'admin';
type AccountPortal = Exclude<Portal, 'admin'>;
type AuthOperation = 'password_login' | 'signup' | 'password_recovery' | 'resend_verification';
type AuthErrorLike = { code: string; message: string; status?: number };
type SessionClient = {
  auth: {
    setSession: (session: { access_token: string; refresh_token: string }) => Promise<{
      data: { user: User | null; session: Session | null };
      error: { message?: string; status?: number; code?: string } | null;
    }>;
  };
};

type PortalPasswordClient = {
  functions: {
    invoke: (name: string, options: { body: Record<string, unknown> }) => Promise<{
      data: Record<string, unknown> | null;
      error: { message?: string } | null;
    }>;
  };
};

type PublicAuthResponse = {
  ok?: boolean;
  code?: string;
  message?: string;
  user?: User | null;
  session?: { access_token?: string; refresh_token?: string } | null;
};

function actionFor(operation: AuthOperation, portal: Portal) {
  if (operation === 'password_login') {
    if (portal === 'hire') return RECAPTCHA_ACTIONS.hireLogin;
    if (portal === 'seek') return RECAPTCHA_ACTIONS.seekLogin;
    return RECAPTCHA_ACTIONS.adminLogin;
  }
  if (operation === 'signup') {
    return portal === 'hire' ? RECAPTCHA_ACTIONS.hireSignup : RECAPTCHA_ACTIONS.seekSignup;
  }
  return operation === 'password_recovery'
    ? RECAPTCHA_ACTIONS.passwordRecovery
    : RECAPTCHA_ACTIONS.resendVerification;
}

function safeError(body: PublicAuthResponse | null, status?: number): AuthErrorLike {
  return {
    code: body?.code || 'PUBLIC_AUTH_FAILED',
    message: body?.message || 'Unable to complete this request. Please try again.',
    ...(status ? { status } : {}),
  };
}

async function invokePublicAuth(
  operation: AuthOperation,
  portal: Portal,
  body: Record<string, unknown>,
): Promise<{ body: PublicAuthResponse | null; error: AuthErrorLike | null }> {
  let recaptchaProof: Awaited<ReturnType<typeof createRecaptchaRequestProof>>;
  try {
    recaptchaProof = await createRecaptchaRequestProof(actionFor(operation, portal));
  } catch {
    return {
      body: null,
      error: { code: 'REQUEST_VERIFICATION_FAILED', message: RECAPTCHA_PUBLIC_ERROR },
    };
  }

  try {
    const requestBody = { ...body };
    delete requestBody.recaptchaToken;
    delete requestBody.recaptchaMode;
    const untrustedReturnTo = requestBody.returnTo;
    delete requestBody.returnTo;
    if (portal === 'seek' && operation === 'signup') {
      const returnTo = sanitizeForkSeekContinuation(untrustedReturnTo, 'seek_signup');
      if (returnTo) requestBody.returnTo = returnTo;
    } else if (portal === 'seek' && operation === 'resend_verification') {
      const returnTo = sanitizeForkSeekContinuation(untrustedReturnTo, 'seek_resend');
      if (returnTo) requestBody.returnTo = returnTo;
    }
    const response = await fetch(buildFunctionUrl('public-auth'), {
      method: 'POST',
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      headers: {
        'Content-Type': 'application/json',
        'apikey': getSupabasePublishableKey(),
        [RECAPTCHA_MODE_HEADER]: recaptchaProof.mode,
      },
      body: JSON.stringify({
        operation,
        portal,
        ...requestBody,
        ...recaptchaTokenPayload(recaptchaProof),
      }),
    });
    const contentType = response.headers.get('content-type')?.split(';', 1)[0]?.trim();
    if (contentType !== 'application/json') {
      return {
        body: null,
        error: { code: 'PUBLIC_AUTH_UNAVAILABLE', message: 'Unable to complete this request. Please try again.' },
      };
    }
    const responseBody = await response.json().catch(() => null) as PublicAuthResponse | null;
    if (!response.ok || responseBody?.ok !== true) {
      return { body: responseBody, error: safeError(responseBody, response.status) };
    }
    return { body: responseBody, error: null };
  } catch {
    return {
      body: null,
      error: { code: 'PUBLIC_AUTH_UNAVAILABLE', message: 'Unable to complete this request. Please try again.' },
    };
  }
}

export async function protectedSignUp(
  _client: SessionClient,
  input: {
    portal: AccountPortal;
    email: string;
    visibleEmail: string;
    password: string;
    fullName: string;
    phone?: string;
    nationalId?: string;
    returnTo?: string | null;
  },
): Promise<{
  data: { user: User | null; session: Session | null };
  error: AuthErrorLike | null;
}> {
  const { body, error } = await invokePublicAuth('signup', input.portal, input);
  if (error) return { data: { user: null, session: null }, error };

  const session = body?.session;
  if (session?.access_token && session.refresh_token) {
    // Signup is verification-gated. A server configuration that returns a
    // session before the one-time confirmation link is consumed must fail
    // closed rather than silently bypassing the email-verification protocol.
    return {
      data: { user: null, session: null },
      error: {
        code: 'UNEXPECTED_SIGNUP_SESSION',
        message: 'Unable to complete this request. Please try again.',
      },
    };
  }

  return { data: { user: body?.user ?? null, session: null }, error: null };
}

export async function protectedPasswordLogin(
  client: SessionClient,
  input: {
    portal: Portal;
    email: string;
    visibleEmail: string;
    password: string;
  },
): Promise<{
  data: { user: User | null; session: Session | null };
  error: AuthErrorLike | null;
}> {
  const { body, error } = await invokePublicAuth('password_login', input.portal, input);
  if (error) return { data: { user: null, session: null }, error };

  const session = body?.session;
  if (!session?.access_token || !session.refresh_token) {
    return {
      data: { user: null, session: null },
      error: {
        code: 'INVALID_LOGIN_SESSION',
        message: 'Unable to complete this request. Please try again.',
      },
    };
  }

  const applied = await client.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  if (applied.error) {
    return {
      data: { user: null, session: null },
      error: {
        code: applied.error.code || 'SESSION_ESTABLISHMENT_FAILED',
        message: 'Unable to complete this request. Please try again.',
        status: applied.error.status,
      },
    };
  }
  return { data: applied.data, error: null };
}

export function resolvePasswordReauthenticationVisibleEmail(user: User) {
  const metadataEmail = typeof user.user_metadata?.visible_email === 'string'
    ? user.user_metadata.visible_email.trim().toLowerCase()
    : '';
  if (metadataEmail && !metadataEmail.includes('@auth.fork.internal')) return metadataEmail;

  const authEmail = user.email?.trim().toLowerCase() ?? '';
  return authEmail && !authEmail.includes('@auth.fork.internal') ? authEmail : '';
}

export async function protectedResendVerification(input: {
  portal: AccountPortal;
  email: string;
  visibleEmail: string;
  returnTo?: string | null;
}) {
  const { error } = await invokePublicAuth('resend_verification', input.portal, input);
  return { error };
}

export async function protectedPasswordRecovery(input: {
  portal: AccountPortal;
  email: string;
  visibleEmail: string;
}) {
  const { error } = await invokePublicAuth('password_recovery', input.portal, input);
  return { error };
}

function portalPasswordError(
  data: Record<string, unknown> | null,
  fallback = 'Unable to complete this request. Please try again.',
): AuthErrorLike {
  return {
    code: typeof data?.code === 'string' ? data.code : 'PORTAL_PASSWORD_UNAVAILABLE',
    message: fallback,
  };
}

export async function getCanonicalPortalPasswordStatus(
  client: PortalPasswordClient,
  portal: AccountPortal,
) {
  const { data, error } = await client.functions.invoke('portal-password', {
    body: { action: 'status', portal },
  });
  if (error || data?.ok !== true || typeof data.hasPassword !== 'boolean') {
    return { hasPassword: null, error: portalPasswordError(data) };
  }
  return { hasPassword: data.hasPassword, error: null };
}

export async function setCanonicalPortalPassword(
  client: PortalPasswordClient,
  input: { portal: AccountPortal; password: string },
) {
  const { data, error } = await client.functions.invoke('portal-password', {
    body: { action: 'set', portal: input.portal, password: input.password },
  });
  if (error || data?.ok !== true) return { error: portalPasswordError(data) };
  return { error: null };
}
